import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./city-card-big.module.scss";
import CityCardButtons from "../city-card-buttons/city-card-buttons";
import ImgSwiper from "../../../../../shared/img-swiper/img-swiper";
import closeIcon from "../../../../../images/close.svg";
import { City } from "../../../../../interfaces/city";
import { useAppDispatch } from "../../../../../hooks/useAppDispatch";
import {
  clearSelectedGraphic,
  extentEstateEn,
} from "../../../../../services/actions/map";
import chevron from "../../../../../images/chevron-left.svg";
import {
  clearOpenedCity,
  setOpenedCity,
} from "../../../../../services/reducers/cities";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../../../hooks/useAppSelector";
import CustomScrollbar from "../../../../ui-kit/scrollbar/scrollbar";
import highway from "../../../../../images/city-select/cars.svg";
import port from "../../../../../images/city-select/ship.svg";
import airport from "../../../../../images/city-select/plane.svg";
import sector from "../../../../../images/city-select/infrastructure.svg";
import rent from "../../../../../images/city-select/rent.svg";
import companies from "../../../../../images/city-select/companies.svg";
import products from "../../../../../images/city-select/products.svg";
import { removeIndustrialCity } from "../../../../../utils/helpers-for-cities";
import { Attachment } from "../../../../../interfaces/feature-info";
import { getFeatureInfo } from "../../../../../services/api";
import { setCurrentOpenedCardPage } from "../../../../../services/reducers/filters";
import { MapSection } from "../../../../../services/reducers/map";

interface Props {
  city: City;
  closeLeftPanel?: (closeLeftPanel: boolean) => void;
}

export default function CityCardBig({ city, closeLeftPanel }: Props) {
  const { t } = useTranslation();
  const [images, setImages] = useState<Attachment[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { openedFromMap, previousOpenedCity } = useAppSelector(
    (state) => state.cities,
  );
  const { cleanedName, wasRemoved } = removeIndustrialCity(city.name);
  const openedCardFilter = useAppSelector(
    (state) => state.filters.selectedCard,
  );
  const navigate = useNavigate();
  const { mapComponentReady } = useAppSelector((state) => state.map);

  useEffect(() => {
    const currentEid = searchParams.get("eid");

    if (city.estateEn && currentEid !== String(city.estateEn)) {
      const params = new URLSearchParams(searchParams);
      params.set("eid", String(city.estateEn));
      navigate(`?${params.toString()}`, { replace: false });
    }
  }, [city.estateEn]);

  useEffect(() => {
    if (city.name && mapComponentReady === MapSection.search) {
      dispatch(extentEstateEn(city.estateEn));
    }
  }, [city, mapComponentReady]);

  useEffect(() => {
    if (!city.estateEn) return;
    const fetchData = async () => {
      const res = await getFeatureInfo(city.layerId, city.estateEn);
      if (!res) return;

      const attachments = res.attachments ?? [];
      setImages(
        attachments.filter((a: Attachment) => a.kindId === 1 && a.typeId === 3),
      );
    };
    fetchData();

    return () => {
      setImages([]);
    };
  }, [city]);

  const handleToggleVisibility = () => {
    if (closeLeftPanel) {
      closeLeftPanel(false);
    }
  };

  function onCloseEstate() {
    dispatch(clearOpenedCity());

    const params = new URLSearchParams(searchParams);

    if (
      openedFromMap &&
      previousOpenedCity &&
      previousOpenedCity.estateEn !== city.estateEn
    ) {
      dispatch(setOpenedCity(previousOpenedCity));
    } else {
      params.delete("eid");
    }
    setSearchParams(params);
    dispatch(clearSelectedGraphic());
    if (openedCardFilter) {
      dispatch(setCurrentOpenedCardPage("filter"));
    }
  }

  return (
    <div className={styles.cityCardBig}>
      <div className={styles.container}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <h2 className={styles.cityName}>
              {cleanedName}
              {wasRemoved && <span> {t("city-card.industrialCity")}</span>}
            </h2>
            {/*<div className={styles.navigate} />*/}
            <img
              onClick={onCloseEstate}
              className={styles.closeBtn}
              src={closeIcon}
              alt="closeBtn"
            />
          </div>
          <p
            className={styles.totalArea}
          >{`${t("city-card.totalArea")} ${city.totalArea} m²`}</p>
        </div>
        <div className={styles.swiper}>
          <ImgSwiper images={images} />
        </div>
        <div className={styles.buttons}>
          <CityCardButtons
            plotsAvailable={city.vacantPlots!}
            spacesForRentAvailable={city.spacesForRent!}
            investmentOpportunities={city.investmentPlots!}
            estateEn={city.estateEn!}
            estateName={city.name}
          />
        </div>
      </div>
      <CustomScrollbar>
        <div className={styles.scrollContent}>
          <div className={styles.cardInfo}>
            <ul className={styles.infoList}>
              <li className={`${styles.infoItem} ${styles.highway}`}>
                <h4 className={styles.name}>
                  <img src={highway} alt="highway" />
                  {t("city-card.majorHighway")}
                </h4>
                <p className={styles.info}>{city.description?.highway}</p>
              </li>
              <li className={`${styles.infoItem} ${styles.port}`}>
                <h4 className={styles.name}>
                  <img src={port} alt="port" />
                  {t("city-card.portFacility")}
                </h4>
                <p className={styles.info}>{city.description?.port}</p>
              </li>
              <li className={`${styles.infoItem} ${styles.airport}`}>
                <h4 className={styles.name}>
                  <img src={airport} alt="airport" />
                  {t("city-card.airport")}
                </h4>
                <p className={styles.info}>{city.description?.airport}</p>
              </li>
            </ul>
            <ul className={styles.infoList}>
              <li className={`${styles.infoItem} ${styles.sector}`}>
                <h4 className={styles.name}>
                  <img src={sector} alt="sector" />
                  {t("city-card.sectorFocus")}
                </h4>
                <p className={styles.info}>{city.description?.sectorFocus}</p>
              </li>
              <li className={`${styles.infoItem} ${styles.rent}`}>
                <h4 className={styles.name}>
                  <img src={rent} alt="rent" />
                  {t("city-card.rentValue")}
                </h4>
                <p className={styles.info}>{city.description?.rentValue}</p>
              </li>
            </ul>
            <ul className={styles.infoList}>
              <li className={`${styles.infoItem} ${styles.companies}`}>
                <h4 className={styles.name}>
                  <img src={companies} alt="companies" />
                  {city.description?.companies} {t("city-card.companies")}
                </h4>
              </li>
              <li className={`${styles.infoItem} ${styles.products}`}>
                <h4 className={styles.name}>
                  <img src={products} alt="products" />
                  {city.description?.products} {t("city-card.products")}
                </h4>
              </li>
            </ul>
          </div>
        </div>
      </CustomScrollbar>
      {closeLeftPanel && (
        <button
          className={styles.toggleButton}
          onClick={handleToggleVisibility}
        >
          <img src={chevron} alt="Toggle panel" />
        </button>
      )}
      <a
        className={styles.downloadLink}
        href="/investors-guide2022.pdf"
        download="investors-guide2022.pdf"
      >
        {t("city-card.investorGuidelines")}
      </a>
    </div>
  );
}
